import _ from 'lodash'
import JURISTIC_TYPE from '../../constants/juristic-type.constant'
import { Inject, Injectable, NgZone } from '@angular/core'
import Ratio from 'lb-ratio'
import { DecimalPipe } from '@angular/common'
import { IParcelState } from '../../features/parcels/components/parcel-state-filter/interfaces/parcel-state.interface'
import { Store } from '@ngrx/store'
import { ActionProgressDialogActions } from '../../shared/dialogs/action-progress-dialog/state/action-types'
import { selectActionProgressDialogFeature } from '../../shared/dialogs/action-progress-dialog/state/action-progress-dialog.selectors'
import { take } from 'rxjs/operators'

@Injectable({
    providedIn: 'root'
})
export class CommonService {

    constructor(@Inject(Store) private store: Store,
                @Inject(DecimalPipe) private decimalPipe: DecimalPipe,
                @Inject(NgZone) protected ngZone: NgZone,
    ) {
    }

    getParcelStatesHierarchy(): IParcelState[] {
        return [
            {name: 'pacht neuzavřen (užívání bez smlouvy)', id: 7},
            {name: 'pachtýř', id: 1},
            {name: 'neznámí vlastníci', id: 9},
            {name: 'neznámí většinoví vlastníci', id: 10},
            {
                name: 'v majetku (stavy parcel)',
                id: 2,
                children: [
                    {name: 'propachtováno', id: 5},
                    {name: 'v řešení', id: 8},
                    {name: 'neurčeno', id: 3},
                    {name: 've vlastním užívání', id: 4},
                    {name: 'ostatní', id: 6}
                ]
            }
        ]
    }

    getParcelStatesBits(): any {
        return [
            //1
            {id: JURISTIC_TYPE.bitNeurceno, name: 'neurčeno', order: 4, editable: true, filterId: 3},
            //2
            {id: JURISTIC_TYPE.bitVlastniUzivani, name: 've vlastním užívání', order: 5, editable: true, filterId: 4},
            //4
            {id: JURISTIC_TYPE.bitPropachtovatel, name: 'propachtováno', order: 2, editable: false, filterId: 5},
            //8
            {id: JURISTIC_TYPE.bitOstatni, name: 'ostatní', order: 6, editable: true, filterId: 6},
            //16
            {id: JURISTIC_TYPE.bitVReseni, name: 'v řešení', order: 3, editable: false, filterId: 8}
        ]
    }

    showActionProgress(message: string): void {
        this.ngZone.run(() => {
            this.store.dispatch(ActionProgressDialogActions.showDialog({show: true, text: message}))
        })
    }

    hideActionProgress(): void {
        this.ngZone.run(() => {
            this.store.dispatch(ActionProgressDialogActions.resetState())
        })
    }

    updateActionProgress(text: string): void {
        this.ngZone.run(() => {
            this.store.select(selectActionProgressDialogFeature).pipe(take(1)).subscribe( state => {
                if (state.show) {
                    this.store.dispatch(ActionProgressDialogActions.setText({text}))
                } else {
                    this.showActionProgress(text)
                }
            })
        })
    }

    localeSensitiveComparator(v1, v2): any {
        if (v1.type === 'number' && v2.type === 'number') {
            if (v1.value === v2.value) {
                return 0
            } else if (v1.value < v2.value) {
                return -1
            } else {
                return 1
            }
        }
        // If we don't get strings, just compare by index
        if (v1.type !== 'string' || v2.type !== 'string') {
            return (v1.index < v2.index) ? -1 : 1
        }

        // Compare strings alphabetically, taking locale into account
        return v1.value.localeCompare(v2.value, 'cs-CZ')
    }

    getYesNoVariants(): any {
        return [
            {value: true, title: 'ano'},
            {value: false, title: 'ne'}
        ]
    }

    getYesNoVariantTitle(value): string {
        if (value) {
            return 'ano'
        } else {
            return 'ne'
        }
    }

    getYearMonthUnits(): any {
        return [
            {value: 'Y', title: 'let'},
            {value: 'M', title: 'měsíců'}
        ]
    }

    getYearMonthUnitTitle(value): any {
        var ymOptions = {'Y': 'let', 'M': 'měsíců'}

        return ymOptions[value]
    }

    compareParcelOwnershipVersions(parcel, ownership): boolean {
        return ((!parcel.endDate && !ownership.endDate) || (parcel.endDate
            && (ownership.startDate === parcel.startDate
                || (ownership.startDate < parcel.startDate && (ownership.endDate > parcel.startDate || !ownership.endDate)))))
    }

    cleanParcelOwnership(parcel, forceEmpty): void {
        if (forceEmpty) {
            parcel.ownerships = []
            parcel.ownershipChanges = []
            parcel.totalFraction = (new Ratio(0, 1, true)).toString()
            parcel.totalFractionSJM = (new Ratio(0, 1, true)).toString()
            parcel.totalFractionAll = (new Ratio(0, 1, true)).toString()
            parcel.totalVymeraFraction = 0
        } else {
            if (!parcel.ownerships) {
                parcel.ownerships = []
            }
            if (!parcel.ownershipChanges) {
                parcel.ownershipChanges = []
            }
            if (!parcel.totalFraction) {
                parcel.totalFraction = (new Ratio(0, 1, true)).toString()
            }
            if (!parcel.totalFractionSJM) {
                parcel.totalFractionSJM = (new Ratio(0, 1, true)).toString()
            }
            if (!parcel.totalVymeraFraction) {
                parcel.totalVymeraFraction = 0
            }
        }
    }

    clearHangingTooltips(): void {
        const tooltips = document.querySelectorAll('.jsTooltipContainer')
        if (tooltips) {
            // HTML collection is "live" so it has to deleted backwards
            _.eachRight(tooltips, (tooltip) => {
                // tooltip.offsetParent !== null means element is visible => delete it
                // @ts-ignore
                if (typeof tooltip === 'object' && tooltip.offsetParent !== null) {
                    tooltip.remove()
                }
            })
        }
    }

    displayContractOwners(contract): boolean {
        var displayContractOwners = false
        contract.tenants?.forEach(tenant => { // Are any of the contract tenants concerned? If so, display owners.
            if (tenant.isConcerned) {
                displayContractOwners = true
            }
        })

        //If no concerned tenant (and is any) then check if neither concenred owner exists, then force to display owners
        if (!displayContractOwners && _.size(contract.tenants) > 0) {
            var neitherDisplayOwners = true
            contract.owners?.forEach((owner) => {
                if (owner.isConcerned) {
                    neitherDisplayOwners = false
                }
            })
            return neitherDisplayOwners
        }

        return displayContractOwners
    }

    displayContractTenants(contract): boolean {
        var displayContractOwnersVar = this.displayContractOwners(contract)

        var displayContractTenants = false
        if (!displayContractOwnersVar) { // If a subject is both owner and tenant, display owners.
            contract.owners?.forEach((owner) => { // Are any of the contract owners concerned? If so, display tenants.
                if (owner.isConcerned) {
                    displayContractTenants = true
                    return false
                }
            })
        }

        return displayContractTenants
    }

    getOwnershipTooltipContent(parcel): string {
        var content = ''
        if (parcel.ownerships) {
            for (var i = 0; i < parcel.ownerships.length; i++) {
                content += '<span>' + (i > 0 ? ' +' : '') + ' '
                    + parcel.ownerships[i].podilCitatel + '/'
                    + parcel.ownerships[i].podilJmenovatel + '</span>'
            }
        }
        return content
    }

    /**
     * Display text in correct czech form according to given number (%d replaced with number)
     * @param num
     * @param text0
     * @param text1
     * @param text2to4
     * @param text5more
     * @returns {*}
     */
    displayCzechSentenceWithNumber(num, text0, text1, text2to4, text5more): string {
        if (!num) {
            return text0.replace('%d', num)
        } else if (num === 1) {
            return text1.replace('%d', num)
        } else if (num >= 2 && num <= 4) {
            return text2to4.replace('%d', num)
        } else {
            return text5more.replace('%d', this.decimalPipe.transform(num, '1.0-0'))
        }
    }

    /**
     * Compares keys of two objects
     * @param a
     * @param b
     * @returns {Boolean} true if objects have the same keys, false otherwise
     */
    compareKeys(a, b): boolean {
        if (!_.isObject(a) || !_.isObject(b)) {
            return false
        }
        var aKeys = Object.keys(a).sort()
        var bKeys = Object.keys(b).sort()
        return JSON.stringify(aKeys) === JSON.stringify(bKeys)
    }

    /**
     * Overwrites obj1's values with obj2's. Final object has structure of obj1
     * @param obj1
     * @param obj2
     * @returns obj3 a new object based on obj1 and obj2,
     * if obj2 is not object, returns obj1
     * if obj1 is not object, returns undefined
     */
    mergeAttributes(obj1, obj2): any {
        if (!_.isObject(obj2)) {
            return obj1
        }
        if (!_.isObject(obj1)) {
            return undefined
        }
        let obj3 = {}
        // all attributes from obj1
        for (let obj1Attrname in obj1) {
            obj3[obj1Attrname] = obj1[obj1Attrname]
        }
        for (let obj2Attrname in obj2) {
            // overwrite obj1 value with obj2 value
            if (!_.isUndefined(obj1[obj2Attrname])) {
                obj3[obj2Attrname] = obj2[obj2Attrname]
            }
        }
        return obj3
    }

    /**
     * Adds value to array or removes it if value exists in array
     * @param {Array} targetArray
     * @param value
     */
    addOrRemoveValue(targetArray, value): void {
        let idx = targetArray.indexOf(value)
        if (idx > -1) {
            targetArray.splice(idx, 1)
        } else {
            targetArray.push(value)
        }
    }

    addOrRemoveObjectByAttribute(targetArray, attribute, object): void {
        var removed = false
        _.remove(targetArray, (item) => {
            if (item[attribute] === object[attribute]) {
                removed = true
            }
            return item[attribute] === object[attribute]
        })

        if (removed === false) {
            targetArray.push(object)
        }
    }

    containsItemByAttribute(array, attribute, object): boolean {
        var contains = false
        for (var i = 0; i < array.length; i++) {
            if (array[i][attribute] === object[attribute]) {
                contains = true
                break
            }
        }
        return contains
    }

    getListOfYearsFromTo(from, to): any[] {
        if (from === to) {
            return [from]
        } else {
            return _.range(from, to + 1, 1)
        }
    }

    getBit(number, bitPos): number {
        return (number >> bitPos) & 1
    }

    setBit(number, bitPos, value): number {
        if (value) {
            number |= (1 << bitPos)
        } else {
            number &= ~(1 << bitPos)
        }
        return number
    }

    getJuristicTypeBitName(bitPos, bits): any {
        return _.find(bits, (bit) => {
            return bit.id === bitPos
        })['name']
    }

    getUserJuristicTypeForSelect(number: number, optionBit: number): number {
        if (optionBit === JURISTIC_TYPE.bitNeurceno) {
            number = this.setBit(number, JURISTIC_TYPE.bitNeurceno, true)
            number = this.setBit(number, JURISTIC_TYPE.bitVlastniUzivani, false)
            number = this.setBit(number, JURISTIC_TYPE.bitOstatni, false)
        } else if (optionBit === JURISTIC_TYPE.bitVlastniUzivani) {
            number = this.setBit(number, JURISTIC_TYPE.bitVlastniUzivani, true)
            number = this.setBit(number, JURISTIC_TYPE.bitOstatni, false)
        } else if (optionBit === JURISTIC_TYPE.bitOstatni) {
            number = this.setBit(number, JURISTIC_TYPE.bitOstatni, true)
            number = this.setBit(number, JURISTIC_TYPE.bitVlastniUzivani, false)
        }

        return number
    }

    createHtmlListFromArray(array): string {
        var html = ''
        if (_.isArray(array) && !_.isEmpty(array)) {
            html += '<ul>'
            array.forEach(item => {
                html += '<li>' + item + '</li>'
            })
            html += '</ul>'
        }
        return html
    }
}

